<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { usePartnerStore } from '@/stores/usePartnerStore'
import type { Partner } from '@/api-swagger'
import { isSiretOrSiren } from '@/utils/textUtils'

defineProps<{
  modelValue: number | string | undefined
  required?: boolean
  errorMessage?: string
}>()

const partnerStore = usePartnerStore()
const { resultSiretValue, searchPartners } = storeToRefs(partnerStore)

const emit = defineEmits(['update:modelValue'])
const inputValue = ref('')
const showResults = ref(false)
const isLoading = ref(false)
const selectedPartner = ref<Partner | null>(null)

let debounceTimeout: ReturnType<typeof setTimeout> | null = null

const handleInput = (event: Event) => {
  isLoading.value = true
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }

  debounceTimeout = setTimeout(async () => {
    const value = (event.target as HTMLInputElement).value
    inputValue.value = value

    if (value === '' && selectedPartner.value) {
      selectedPartner.value = null
      emit('update:modelValue', null)
      isLoading.value = false
      return
    }

    resultSiretValue.value = []
    if (isSiretOrSiren(value)) {
      await partnerStore.searchSiretOnly(value.replace(' ', ''))
    } else if (value.length >= 3) {
      await partnerStore.searchPartnerByText(value)
      if (searchPartners.value && searchPartners.value.length > 0) {
        resultSiretValue.value = searchPartners.value || []
      }
    }

    isLoading.value = false
    showResults.value = true
  }, 500)
}

const selectPartner = (partner: Partner) => {
  selectedPartner.value = partner
  inputValue.value = ''
  showResults.value = false
  emit('update:modelValue', partner.id)
}

const removePartner = () => {
  selectedPartner.value = null
  resultSiretValue.value = []
  emit('update:modelValue', null)
}

const handleFocus = () => {
  if (inputValue.value.length >= 3) {
    showResults.value = true
  }
}
</script>

<template>
  <div class="relative">
    <div class="label" for="partner-search">
      <span class="label-text font-semibold">
        Raison sociale
        <span v-if="required" class="text-red-500">*</span>
      </span>
    </div>

    <div class="input input-sm input-bordered w-full" :class="{ 'border-red-500': errorMessage }">
      <div v-if="selectedPartner" class="badge badge-primary">
        <span class="mr-1">{{ selectedPartner.name }} ({{ selectedPartner.siret }})</span>
        <button @click="removePartner" type="button">&times;</button>
      </div>

      <input
        id="partner-search"
        type="text"
        v-model="inputValue"
        :placeholder="selectedPartner ? '' : 'Recherche...'"
        @input="handleInput"
        @focus="handleFocus"
      />
    </div>

    <div v-if="isLoading" class="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg py-2 px-3 flex items-center justify-center">
      <span class="loading loading-spinner loading-xs me-2"></span> Recherche en cours
    </div>

    <div
      v-if="showResults && !isLoading && inputValue.length >= 3 && resultSiretValue && resultSiretValue.length > 0"
      class="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-80 overflow-y-auto"
    >
      <div
        v-for="partner in resultSiretValue"
        :key="partner.id"
        class="px-3 py-2 hover:bg-gray-100 cursor-pointer"
        @mousedown="selectPartner(partner)"
      >
        <div class="font-medium">{{ partner.name }} ({{ partner.siret || '' }})</div>
      </div>
    </div>

    <div
      v-if="showResults && !isLoading && inputValue.length < 3"
      class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md py-2 px-3"
    >
      3 caractères minimum.
    </div>

    <div
      v-if="showResults && !isLoading && inputValue.length > 0 && resultSiretValue && resultSiretValue.length === 0"
      class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md py-2 px-3"
    >
      Aucun résultat trouvé pour "{{ inputValue }}"
    </div>
    <span v-if="errorMessage" class="text-red-500 text-sm">{{ errorMessage }}</span>
  </div>
</template>
